import { TimeZone } from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';

import { data as enums } from '~constants/enums';
import { Nullable } from '~types/Nullable';

// Todo: USE User settings for date in right format
const dateFormat = (date: Date | Dayjs | string, format?: string) => {
  const dateFormat = format || enums.date_format.default;

  return dayjs(date).tz().format(dateFormat);
};

const formatDuration = (
  seconds: number,
  showSeconds?: boolean,
  spacer?: string,
  useLongFormat?: boolean,
) => {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  const daysSuffix = useLongFormat ? ' days' : 'd';
  const hoursSuffix = useLongFormat ? ' hours' : 'h';
  const minutesSuffix = useLongFormat ? ' minutes' : 'm';
  const secondsSuffix = useLongFormat ? ' seconds' : 's';

  const formattedDuration = [
    days > 0 ? `${days}${daysSuffix}` : '',
    hours > 0 ? `${hours}${hoursSuffix}` : '',
    minutes > 0 ? `${minutes}${minutesSuffix}` : '',
    showSeconds && remainingSeconds > 0 ? `${remainingSeconds}${secondsSuffix}` : '',
  ]
    .filter(Boolean)
    .join(spacer || ' ');

  return formattedDuration;
};

/**
 * Converts minutes to hours and minutes format.
 * @param minutes - The number of minutes to convert.
 * @returns The formatted string in the format "hh:mm".
 */
function minutesToHoursMins(minutes: number): string {
  const hh =
    Math.floor(minutes / 60)
      ?.toString()
      ?.padStart(2, '0') ?? '00';
  const mm = (minutes % 60)?.toString()?.padStart(2, '0') ?? '00';

  return `${hh}:${mm}`;
}

/**
 * Converts whole minutes to decimal hours, rounded to hundredths of an hour — the precision
 * the hours inputs accept. E.g. 570 → 9.5.
 * @param minutes - The number of minutes to convert.
 * @returns The rounded number of hours, or null when there are no minutes.
 */
function minutesToDecimalHours(minutes: Nullable<number>) {
  if (minutes === null || minutes === undefined) {
    return null;
  }

  return Number((minutes / 60).toFixed(2));
}

/**
 * Merges date and time values into an ISO string.
 * @param date - The date value (Date, Dayjs, or string)
 * @param time - The time value (Date, Dayjs, or string)
 * @returns ISO string combining the date and time
 */
function mergeDateTime(date: Date | Dayjs | string, time: Date | Dayjs | string): string {
  const dateFragment = dayjs.tz(date);
  const timeFragment = dayjs.tz(time);

  return dateFragment
    .set('hour', timeFragment.hour())
    .set('minute', timeFragment.minute())
    .set('second', 0)
    .toISOString();
}

export const allowedTimezones = [
  TimeZone.AMERICA_SAO_PAULO,
  TimeZone.CANADA_ATLANTIC,
  TimeZone.CANADA_CENTRAL,
  TimeZone.CANADA_EASTERN,
  TimeZone.CANADA_MOUNTAIN,
  TimeZone.CANADA_NEWFOUNDLAND,
  TimeZone.CANADA_PACIFIC,
  TimeZone.CANADA_SASKATCHEWAN,
  TimeZone.CANADA_YUKON,
  TimeZone.US_ALASKA,
  TimeZone.US_ALEUTIAN,
  TimeZone.US_ARIZONA,
  TimeZone.US_CENTRAL,
  TimeZone.US_EASTERN,
  TimeZone.US_EAST_INDIANA,
  TimeZone.US_HAWAII,
  TimeZone.US_INDIANA_STARKE,
  TimeZone.US_MICHIGAN,
  TimeZone.US_MOUNTAIN,
  TimeZone.US_PACIFIC,
  TimeZone.US_SAMOA,
];

export {
  dateFormat,
  formatDuration,
  mergeDateTime,
  minutesToDecimalHours,
  minutesToHoursMins,
};
